import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { AddOnTextInput, DropdownSelect, Icon, TextInput, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { EditVehicleFormData } from "../form";

export const VehicleDetails = ({ vehicle }: { vehicle: Vehicle }) => {
  const { data: options, isFetching: isFetchingOptions } = useGetVehicleFormOptionsQuery();

  const vehicleTypes = useMemo(() => {
    if (!options?.vehicleTypes) return [];
    return options.vehicleTypes.map((vt) => ({ name: vt.name, value: vt.uuid }));
  }, [options?.vehicleTypes]);

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<EditVehicleFormData>();

  const maxCapacity = vehicle.model.maxCapacity;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Typography>Vehicle has markings?</Typography>
        <Controller
          control={control}
          name="hasMarkings"
          render={({ field }) => <Toggle size="sm" checked={field.value} onChange={field.onChange} />}
        />
      </div>

      <div className="grid grid-cols-2 gap-5 gap-y-6">
        <label htmlFor="vehicleTypeUuid" className="col-span-2 md:col-span-1">
          <Typography className="relative flex items-center gap-2 leading-[2rem]">
            Vehicle Type
            <Tooltip content="Operator-defined vehicle type used within your own fleet setup" placement="top">
              <Icon name="InfoCircle" variant="Bold" size="md" className="relative z-10 rotate-180 text-neutral-dark-gray" />
            </Tooltip>
          </Typography>
          <Controller
            control={control}
            name="vehicleTypeUuid"
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                placeholder="Select Vehicle Type"
                options={vehicleTypes}
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.vehicleTypeUuid}
                disabled={isFetchingOptions}
              />
            )}
          />
          {errors.vehicleTypeUuid && (
            <Typography className="mt-1 text-sm text-danger">{errors.vehicleTypeUuid.message}</Typography>
          )}
        </label>

        <label htmlFor="nickname" className="col-span-2 md:col-span-1">
          <Typography className="leading-[2rem]">Vehicle Nickname (Optional)</Typography>
          <TextInput
            {...register("nickname")}
            id="nickname"
            placeholder="Enter Vehicle Nickname"
            className="[&>div]:w-full [&_input]:w-full"
          />
        </label>

        <label htmlFor="capacity" className="col-span-2 md:col-span-1">
          <Typography className="leading-[2rem]">Passenger Capacity</Typography>
          <Controller
            control={control}
            name="capacity"
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                placeholder="Select Passenger Capacity"
                options={Array.from({ length: maxCapacity }, (_, i) => (i + 1).toString())}
                value={field.value?.toString()}
                onChange={(value: string) => field.onChange(+value)}
              />
            )}
          />
        </label>

        <label htmlFor="bags" className="col-span-2 md:col-span-1">
          <Typography className="leading-[2rem]">Bag Capacity</Typography>
          <Controller
            control={control}
            name="bags"
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                placeholder="Select Number of Capacity"
                options={Array.from({ length: 101 }, (_, i) => i.toString())}
                value={field.value?.toString()}
                onChange={(value: string) => field.onChange(+value)}
              />
            )}
          />
        </label>

        <label htmlFor="vehicleOwnerFee" className="col-span-2 md:col-span-1">
          <Typography className="leading-[2rem]">Vehicle Owner Maintenance Fee (Optional)</Typography>
          <AddOnTextInput
            {...register("vehicleOwnerFee", { valueAsNumber: true })}
            id="vehicleOwnerFee"
            type="number"
            endAddOn="%"
            min={0}
            max={100}
            placeholder="0"
            className="w-full [&>div]:flex-1"
          />
          <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
            Portion of the driver payment paid directly to the owner of the vehicle
          </Typography>
        </label>
      </div>
    </div>
  );
};
